import { lazy, Suspense, type ReactNode } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { useAuth } from '@/lib/auth'
import { COPY } from '@/lib/copy'
import { BoldfitMark } from '@/components/boldfit-mark'
import { ErrorBoundary } from '@/components/error-boundary'
import { Shell } from './Shell'

const MessagesList = lazy(() => import('@/screens/messages-list'))
const MessageDetail = lazy(() => import('@/screens/message-detail'))

function LoginScreen() {
  const { signIn, authError, account } = useAuth()
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-4 bg-background text-ink">
      <div className="flex items-center gap-2">
        <BoldfitMark className="h-8 w-auto" />
        <span className="font-display text-meta font-extrabold tracking-[0.28em]">HERALD</span>
      </div>
      <p className="max-w-[320px] text-center text-data text-ink-2">{COPY.signInLead}</p>
      <button
        type="button"
        onClick={() => void signIn()}
        className="rounded-md bg-primary px-5 py-2 text-body font-semibold text-primary-foreground"
      >
        Sign in with Microsoft
      </button>
      {account ? (
        <p className="text-meta text-ink-3">Your session ended. Sign in again as {account.username}.</p>
      ) : null}
      {authError ? (
        <details className="max-w-[360px] text-center text-meta text-crit">
          <summary className="cursor-pointer font-bold">Show details</summary>
          <span className="font-mono break-all">{authError}</span>
        </details>
      ) : null}
    </div>
  )
}

function Gate({ children }: { children: ReactNode }) {
  const { ready, authenticated } = useAuth()
  if (!ready) return null
  if (!authenticated) return <LoginScreen />
  return <>{children}</>
}

export default function App() {
  const routePath = useLocation().pathname
  return (
    <Gate>
      <Shell>
        <ErrorBoundary key={routePath}>
          <Suspense
            fallback={
              <div className="px-5 py-4 text-meta text-ink-3" role="status">
                Loading…
              </div>
            }
          >
            <Routes>
              <Route path="/" element={<Navigate to="/messages" replace />} />
              <Route path="/login" element={<Navigate to="/messages" replace />} />
              <Route path="/messages" element={<MessagesList />} />
              <Route path="/messages/:id" element={<MessageDetail />} />
              <Route
                path="*"
                element={
                  <div className="flex h-full flex-col items-center justify-center gap-2 text-ink-2">
                    <div className="text-section font-extrabold text-ink">There is no page here</div>
                    <Navigate to="/messages" replace />
                  </div>
                }
              />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </Shell>
    </Gate>
  )
}
